/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { OSKUserOrganization } from '@oskey/core/types';
import { OSKSideMenu } from '../types/side-menus.type';
import { OSKSideMenuItem } from '../types/side-menu-item.type';

const organizationMenuItems: OSKSideMenuItem[] = [
  // Entity view menu items
  {
    routerLink: '/entities/:entityId',
    label: 'portals.sidemenu.organization.dashboard',
    icon: 'assets/svg/dashboard-stroke.svg',
    viewType: 'entities'
  },
  {
    label: 'portals.sidemenu.organization.spaceDivider',
    isDivider: true,
    viewType: 'entities'
  },
  {
    routerLink: '/entities/:entityId/properties',
    label: 'portals.sidemenu.organization.properties',
    icon: 'assets/svg/property-stroke.svg',
    viewType: 'entities'
  },
  {
    routerLink: '/entities/:entityId/suppliers',
    label: 'portals.sidemenu.organization.suppliers',
    icon: 'assets/svg/suppliers-stroke.svg',
    userRoles: ['v1.org.suppliers.admin'],
    viewType: 'entities'
  },
  {
    routerLink: '/entities/:entityId/message-center',
    label: 'portals.sidemenu.organization.communications',
    icon: 'assets/svg/chat-stroke.svg',
    userRoles: ['v1.org.communications.admin'],
    viewType: 'entities'
  },
  {
    routerLink: '/entities/:entityId/users',
    label: 'portals.sidemenu.organization.users',
    icon: 'assets/svg/admins-stroke.svg',
    userRoles: ['v1.org.user.admin'],
    viewType: 'entities'
  },

  // Property view menu items
  {
    routerLink: '/entities/:entityId/properties/:propertyId/property-dashboard',
    label: 'portals.sidemenu.organization.dashboard',
    icon: 'assets/svg/dashboard-stroke.svg',
    viewType: 'properties'
  },
  {
    label: 'portals.sidemenu.organization.spaceDivider',
    isDivider: true,
    viewType: 'properties'
  },
  {
    routerLink: '/entities/:entityId/properties/:propertyId/buildings',
    label: 'portals.sidemenu.organization.buildings',
    icon: 'assets/svg/building-stroke.svg',
    viewType: 'properties'
  },

  {
    routerLink: '/entities/:entityId/properties/:propertyId/inhabitants/inhabitant-list',
    label: 'portals.sidemenu.organization.residents',
    icon: 'assets/svg/residents-stroke.svg',
    userRoles: ['v1.org.residents.admin'],
    viewType: 'properties'
  },
  {
    routerLink: '/entities/:entityId/properties/:propertyId/generalRules',
    label: 'portals.sidemenu.organization.generalRules',
    icon: 'assets/svg/manufacturing_fill.svg',
    userRoles: ['v1.org.settings.admin'],
    viewType: 'properties'
  },
  {
    routerLink: '/entities/:entityId/properties/:propertyId/suppliers',
    label: 'portals.sidemenu.organization.suppliers',
    icon: 'assets/svg/suppliers-stroke.svg',
    userRoles: ['v1.org.suppliers.admin'],
    viewType: 'properties'
  },
  {
    routerLink: '/entities/:entityId/properties/:propertyId/message-center',
    label: 'portals.sidemenu.organization.communications',
    icon: 'assets/svg/chat-stroke.svg',
    userRoles: ['v1.org.communications.admin'],
    viewType: 'properties'
  },
  {
    routerLink: '/entities/:entityId/properties/:propertyId/users',
    label: 'portals.sidemenu.organization.users',
    icon: 'assets/svg/admins-stroke.svg',
    userRoles: ['v1.org.user.admin'],
    viewType: 'properties'
  }

  // {
  //   routerLink: '/',
  //   label: 'portals.sidemenu.organization.billing',
  //   icon: 'attach_money',
  //   userRoles: ['v1.org.user.admin']
  // }
];

/**
 * Generates the "Organization portal" default menu based on the user's roles
 *
 * @param userOrganization The user organization for which we are generating the menu
 * @returns {OSKSideMenu} The generated sidemenu
 */
export function generateUserOrganizationDefaultMenu(
  userOrganization: OSKUserOrganization
): OSKSideMenu {
  const defaultMenu: OSKSideMenu = {
    items: []
  };

  if (userOrganization.userRoles.includes('v1.org.admin')) {
    defaultMenu.items.push(...organizationMenuItems);
  } else {
    for (const item of organizationMenuItems) {
      if (!item.userRoles || userOrganization.userRoles.some((r) => item.userRoles?.includes(r))) {
        defaultMenu.items.push(item);
      }
    }
  }

  return defaultMenu;
}
